'use client';

import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import React from 'react';

// Initialize Bugsnag only once
let bugsnagInitialized = false;

export const initBugsnag = () => {
  if (bugsnagInitialized || typeof window === 'undefined') {
    return;
  }

  const apiKey = process.env.NEXT_PUBLIC_BUGSNAG_API_KEY || '2812eaba742b16cd2df348d57615babb';
  
  if (!apiKey) {
    console.warn('Bugsnag API key not configured');
    return;
  }

  try {
    Bugsnag.start({
      apiKey,
      plugins: [new BugsnagPluginReact()],
      releaseStage: process.env.NEXT_PUBLIC_ENV_MODE || process.env.NODE_ENV || 'development',
      enabledReleaseStages: ['production', 'staging', 'development'],
      appVersion: process.env.NEXT_PUBLIC_APP_VERSION || '1.0.0',
      autoDetectErrors: true,
      autoTrackSessions: true,
      // Add user context when available
      onError: (event) => {
        // Get user info from localStorage if available
        try {
          const userStr = localStorage.getItem('supabase.auth.token');
          if (userStr) {
            const userData = JSON.parse(userStr);
            if (userData?.currentSession?.user) {
              event.setUser(
                userData.currentSession.user.id,
                userData.currentSession.user.email,
                userData.currentSession.user.user_metadata?.display_name
              );
            }
          }
        } catch {
          // Ignore errors reading user data
        }

        // Add additional context
        event.addMetadata('app', {
          url: window.location.href,
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString(),
        });

        return true;
      },
    });

    bugsnagInitialized = true;
    console.log('Bugsnag initialized successfully');
  } catch (error) {
    console.error('Failed to initialize Bugsnag:', error);
  }
};

// Get the ErrorBoundary component
export const getErrorBoundary = () => {
  if (typeof window === 'undefined' || !bugsnagInitialized) {
    return null;
  }

  const plugin = Bugsnag.getPlugin('react');
  if (plugin) {
    return plugin.createErrorBoundary(React);
  }
  return null;
};

// Helper to notify Bugsnag of an error manually
export const notifyError = (
  error: Error | string,
  context?: string,
  metadata?: Record<string, any>,
  severity: 'error' | 'warning' | 'info' = 'error'
) => {
  if (typeof window === 'undefined') {
    return;
  }

  const errorObj = typeof error === 'string' ? new Error(error) : error;

  try {
    Bugsnag.notify(errorObj, (event) => {
      if (context) {
        event.context = context;
      }
      if (metadata) {
        event.addMetadata('custom', metadata);
      }
      event.severity = severity;
    });
  } catch (e) {
    console.error('Failed to notify Bugsnag:', e);
  }
};

// Helper to leave a breadcrumb for debugging
export const leaveBreadcrumb = (
  message: string,
  metadata?: Record<string, any>,
  type: 'navigation' | 'request' | 'process' | 'log' | 'user' | 'state' | 'error' | 'manual' = 'manual'
) => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    Bugsnag.leaveBreadcrumb(message, metadata, type);
  } catch {
    // Ignore errors
  }
};

// Helper to set user context
export const setUser = (id?: string, email?: string, name?: string) => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    Bugsnag.setUser(id, email, name);
  } catch {
    // Ignore errors
  }
};

// Helper to clear user context (on logout)
export const clearUser = () => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    Bugsnag.setUser(undefined, undefined, undefined);
  } catch {
    // Ignore errors
  }
};

export default Bugsnag;
